import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { Router, ActivatedRoute } from "@angular/router";
import { ILogin } from "../../interfaces/login";
import { StorageService } from "../../services/storage.service";
import { UserService } from "../../services/user.service";
import { GTMService } from "src/app/services/gtm.service";
import { select, Store } from "@ngrx/store";
import {
  selectMgmIsActive,
  selectVoucherIsActive,
} from "src/app/store/selectors/mgm.selectors";
import {
  getAccounts,
  getActiveBusiness,
  getUser,
} from "src/app/store/selectors/auth.selectors";
import { Observable } from "rxjs/Observable";
import { setActiveBusiness } from "src/app/store/actions/auth.actions";
import { environment } from "../../../environments/environment";
import { CustomerProfileEnum } from "../../enums/CustomerProfileEnum";
import { AuthService } from "../../services/auth.service";
import { WalletService } from "../../services/wallet.service";

// "Saldo em carteira" do dropdown do usuário: mesmo bloco do menu do portal
// (web-applications libs/ui/components/MenuPortal/WalletBalance.tsx).
type WalletBalanceState =
  | { status: "loading" }
  | { status: "ready"; value: string }
  | { status: "error" };

@Component({
  selector: "header-menu",
  templateUrl: "./header-menu.component.html",
  providers: [StorageService, UserService, GTMService],
})
export class HeaderMenuComponent implements OnInit, OnDestroy {
  @ViewChild("modalInvite") public modalInvite: any;
  mgmIsActive$: Observable<boolean>;
  voucherIsActive$: Observable<boolean>;
  user$: Observable<any>;
  accounts$: Observable<any[]>;

  selectedMenuLink: string;
  activeBusiness;
  customerProfile = CustomerProfileEnum;

  public menu: Array<any> = [];
  public options: Array<any> = [];
  public toggleOpenNav = false;

  public login = <ILogin>{};
  public establishmentsWithPendencies = [];
  public establishmentsWaitingApproval = [];
  public showFinancialAlert = false;
  public showPremiumModal = false;
  public currentUser: any = null;
  public walletBalance: WalletBalanceState = { status: "loading" };
  private walletAccountId: string = null;
  private walletSubscription: Subscription;
  private subscriptions = new Subscription();

  public ROLE_COMPANY = "company";
  public ROLE_COLLABORATOR = "user";
  private readonly COMPANY_PROFILE_ID = "5a1ef473694cfe4bc3935212";
  private readonly ENDERECOS_MENU_LINK = "/empresa/enderecos";

  constructor(
    private activateRoute: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private authService: AuthService,
    private gtm: GTMService,
    private store: Store,
    private walletService: WalletService
  ) {}

  ngOnInit() {
    this.mgmIsActive$ = this.store.pipe(select(selectMgmIsActive));
    this.voucherIsActive$ = this.store.pipe(select(selectVoucherIsActive));
    this.user$ = this.store.pipe(select(getUser));
    this.accounts$ = this.store.pipe(select(getAccounts));
    this.subscriptions.add(
      this.store.pipe(select(getActiveBusiness)).subscribe((activeBusiness) => {
        this.activeBusiness = activeBusiness;
      })
    );

    this.subscriptions.add(this.user$.subscribe((user) => {
      this.currentUser = user;
      if (user && user.companies && user.companies[0] && user.profilesId) {
        this.showFinancialAlert =
          user.companies[0].hasPendingPayment &&
          user.profilesId === this.COMPANY_PROFILE_ID;
      }
      this.loadWalletBalance(user);
    }));

    const urlParams = new URLSearchParams(window.location.search);
    const invite = urlParams.get("invite");
    if (invite === "true") {
      this.modalInvite.open();
    }

    this.updateSelectedMenuLink();
  }

  // O header-menu é recriado a cada página: sem isto, cada página visitada deixava um ouvinte de
  // user$ vivo, e cada um buscaria o saldo de novo.
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.walletSubscription) {
      this.walletSubscription.unsubscribe();
    }
  }

  // Busca o saldo uma vez por conta. Começa do último saldo da mesma conta (ou "—"); com um saldo já
  // conhecido, a falha da busca mantém o valor, e sem ele o bloco some.
  private loadWalletBalance(user: any): void {
    const accountId = user ? user.id || user.customerId : null;
    if (!accountId || accountId === this.walletAccountId) {
      return;
    }
    this.walletAccountId = accountId;

    const cached = this.walletService.cachedBalanceFor(accountId);
    this.walletBalance =
      cached === null
        ? { status: "loading" }
        : { status: "ready", value: this.formatCurrency(cached) };

    if (this.walletSubscription) {
      this.walletSubscription.unsubscribe();
    }
    this.walletSubscription = this.walletService.getBalance(accountId).subscribe(
      (availableBalance) => {
        this.walletBalance = {
          status: "ready",
          value: this.formatCurrency(availableBalance),
        };
      },
      () => {
        if (this.walletService.cachedBalanceFor(accountId) === null) {
          this.walletBalance = { status: "error" };
        }
      }
    );
  }

  private formatCurrency(value: number): string {
    return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  openWalletStatement(): void {
    this.trackingEvent("allya", "menu:click", "carteira:ver-extrato");
    window.location.href = `${environment.PORTAL_URL}/carteira`;
  }

  setActiveAccount(business: any) {
    const businessId = business.id;
    const businessType = business.typeBusiness;
    this.store.dispatch(setActiveBusiness({ businessId, businessType }));
    this.navigateToHomeAndReloadPage();
  }

  navigateToHomeAndReloadPage(): void {
    this.router.navigate(["/home"]).then(() => window.location.reload());
  }

  getLogoUser() {
    const account = this.userService.getLoggedAccount();

    if (account) {
      if (this.userService.getTypeAccount() === this.ROLE_COMPANY) {
        if (account.image_key) {
          return environment.CDN_IMAGES_WEB + "/" + account.image_key;
        } else {
          return account.image;
        }
      } else {
        return account.image.logo;
      }
    }

    return null;
  }

  logout(): void {
    this.router.navigate(["/logout"]);
  }

  isLoggedIn() {
    return !!this.userService.getLoggedUser();
  }

  trackingEvent(category, action, label?) {
    this.gtm.interaction(category, action, label ? label.toLowerCase() : "");
  }

  menuIsSelected(item): boolean {
    if (typeof item === "string") {
      return this.selectedMenuLink === item;
    }

    if (item.subItens && item.subItens.length > 0) {
      for (const subItem of item.subItens) {
        if (this.selectedMenuLink === subItem.link) {
          return true;
        }
      }
    }

    if (item.link) {
      return this.selectedMenuLink === item.link;
    }

    return false;
  }

  private updateSelectedMenuLink() {
    const url = this.router.routerState.snapshot.url;
    this.selectedMenuLink = url === "/" ? "/home" : url.split("?")[0];
  }

  navigateToSubscriptionEdition() {
    window.location.href = `${environment.PORTAL_URL}/plano-de-assinatura/editar-cartao?cartao_valido=true`;
  }

  navigateToSubscriptionFremium() {
    window.location.href = `${environment.PORTAL_URL}/assinaturas`;
  }

  navigateToAccountPrivacity() {
    window.location.href = `${environment.PORTAL_URL}/privacidade-da-conta/`;
  }

  navigateToTalkUs() {
    window.open("https://ajuda.allya.com.br/pt-BR/");
  }

  // Função para verificar se o usuário é FREE (baseado na validação do MenuPortal React)
  isFreeUser(user: any): boolean {
    if (!user || !user.companies || user.companies.length === 0) {
      return false;
    }

    // Validação 1: contractModel deve ser "b2c" ou "coparticipation" para ser FREE
    const company = user.companies[0];
    const isFreeCompanyType =
      company && (company.contractModel === 'b2c' || company.contractModel === 'coparticipation');

    // Validação 2: freemiumSubscription.isFree: false = Premium, true = Free
    const isFreeSubscription =
      user.freemiumSubscription && user.freemiumSubscription.isFree === true;

    return !!isFreeCompanyType && !!isFreeSubscription;
  }

  // Função para lidar com o clique no "Indicar Parceiro"
  handleIndicarParceiroClick(event: Event, link: string): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isFreeUser(this.currentUser)) {
      this.showPremiumModal = true;
      return;
    }

    // Se não for FREE, redireciona normalmente
    window.location.href = link;
  }

  closePremiumModal(): void {
    this.showPremiumModal = false;
  }

  getProfileOptions(user: any): any[] {
    if (!user || !user.options || !user.options.length) {
      return [];
    }

    return user.options.filter(
      (option) =>
        !this.isCadastroEmpresaMenuItem(option.name) &&
        !this.isEnderecosMenuItem(option.name)
    );
  }

  getNavModules(user: any): any[] {
    if (!user || !user.modules || !user.modules.length) {
      return [];
    }

    const enderecosFromOptions =
      user.options &&
      user.options.find((option) => this.isEnderecosMenuItem(option.name));
    const enderecosLink =
      (enderecosFromOptions && enderecosFromOptions.link) ||
      this.ENDERECOS_MENU_LINK;

    return user.modules.map((module) => {
      const subItens = (module.subItens || []).filter(
        (subItem) => !this.isAmigoMenuItem(subItem.name)
      );

      if (!this.isGestaoModule(module.name)) {
        return Object.assign({}, module, { subItens: subItens });
      }

      return Object.assign({}, module, {
        subItens: this.mapGestaoSubmenuLinks(
          this.ensureSegmentacaoInGestaoSubmenu(
            this.ensureEnderecosInGestaoSubmenu(subItens, enderecosLink)
          )
        ),
      });
    });
  }

  private mapGestaoSubmenuLinks(subItens: any[]): any[] {
    return subItens.map((subItem) => {
      const name = this.isMateriaisApoioMenuItem(subItem.name)
        ? "Campanhas"
        : subItem.name;

      return Object.assign({}, subItem, {
        name,
        link: this.resolveGestaoSubmenuLink(Object.assign({}, subItem, { name })),
      });
    });
  }

  private resolveGestaoSubmenuLink(subItem: { name?: string; link?: string }): string {
    if (this.isSegmentacaoMenuItem(subItem.name)) {
      return `${environment.PORTAL_URL}/dashboard/segmentacao`;
    }

    if (
      this.isParceriasExclusivasMenuItem(subItem.name) ||
      this.isLegacyParceriasExclusivasLink(subItem.link)
    ) {
      return `${environment.PORTAL_URL}/dashboard/parcerias-exclusivas`;
    }

    return subItem.link;
  }

  getSorteiosMenuLink(): string {
    return `${environment.PORTAL_URL}/sorteios`;
  }

  navigateToSorteios(): void {
    window.location.href = this.getSorteiosMenuLink();
  }

  private isParceriasExclusivasMenuItem(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return (
      normalizedName === "parcerias exclusivas" ||
      normalizedName === "parcerias proprias"
    );
  }

  private isLegacyParceriasExclusivasLink(link: string): boolean {
    if (!link) {
      return false;
    }

    const normalizedLink = link.split("?")[0].toLowerCase();

    if (/^https?:\/\//i.test(link)) {
      return (
        normalizedLink.indexOf("/dashboard/parcerias-proprias") !== -1 ||
        normalizedLink.indexOf("/dashboard/parcerias-exclusivas") !== -1 ||
        normalizedLink.indexOf("/parcerias-proprias") !== -1 ||
        normalizedLink.indexOf("/parcerias-exclusivas") !== -1
      );
    }

    return (
      normalizedLink.indexOf("parcerias-proprias") !== -1 ||
      normalizedLink.indexOf("parcerias-exclusivas") !== -1
    );
  }

  private ensureEnderecosInGestaoSubmenu(
    subItens: any[],
    enderecosLink: string
  ): any[] {
    const withoutEnderecos = subItens.filter(
      (subItem) => !this.isEnderecosMenuItem(subItem.name)
    );
    const cadastroIndex = withoutEnderecos.findIndex((subItem) =>
      this.isCadastroEmpresaMenuItem(subItem.name)
    );
    const enderecosItem = { name: "Endereços", link: enderecosLink };

    if (cadastroIndex === -1) {
      return withoutEnderecos.concat([enderecosItem]);
    }

    const result = withoutEnderecos.slice();
    result.splice(cadastroIndex + 1, 0, enderecosItem);
    return result;
  }

  private ensureSegmentacaoInGestaoSubmenu(subItens: any[]): any[] {
    if (subItens.some((subItem) => this.isSegmentacaoMenuItem(subItem.name))) {
      return subItens;
    }

    const segmentacaoItem = {
      name: "Segmentação",
      link: `${environment.PORTAL_URL}/dashboard/segmentacao`,
    };

    const colaboradoresIndex = subItens.findIndex((subItem) =>
      this.isColaboradoresMenuItem(subItem.name)
    );

    if (colaboradoresIndex === -1) {
      const parceriasIndex = subItens.findIndex((subItem) =>
        this.isParceriasExclusivasMenuItem(subItem.name)
      );

      if (parceriasIndex === -1) {
        return subItens.concat([segmentacaoItem]);
      }

      const result = subItens.slice();
      result.splice(parceriasIndex, 0, segmentacaoItem);
      return result;
    }

    const result = subItens.slice();
    result.splice(colaboradoresIndex + 1, 0, segmentacaoItem);
    return result;
  }

  private normalizeMenuName(name: string): string {
    return (name || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  private isCadastroEmpresaMenuItem(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return (
      normalizedName === "cadastro empresa" ||
      normalizedName === "cadastroempresa"
    );
  }

  private isEnderecosMenuItem(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return normalizedName === "enderecos";
  }

  private isColaboradoresMenuItem(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return normalizedName === "colaboradores";
  }

  private isSegmentacaoMenuItem(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return normalizedName === "segmentacao";
  }

  private isMateriaisApoioMenuItem(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return (
      normalizedName === "materiais de apoio" ||
      normalizedName === "material de apoio"
    );
  }

  private isAmigoMenuItem(name: string): boolean {
    return this.normalizeMenuName(name) === "amigo";
  }

  private isGestaoModule(name: string): boolean {
    const normalizedName = this.normalizeMenuName(name);
    return normalizedName === "gestao";
  }
}
